import { useState } from 'react';

const GALLERY_ITEMS = [
  {
    type: 'image' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/610009ab-605b-40b3-a303-4e68d624ef31/clean-shot-2026-03-08-at-16-49-38-2-x-1920w.webp',
    alt: 'Vela Bay Exterior View',
    span: 'full',
  },
  {
    type: 'image' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/a4a2d395-9a89-4879-bea2-ce0ec1c6b479/clean-shot-2026-03-08-at-16-51-19-2-x-1920w.webp',
    alt: 'Vela Bay Project Rendering',
    span: 'double',
  },
  {
    type: 'video' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/videos/2cf9eec4-920a-481d-83e1-f70df3962ab4/VID-20260122-WA0088.mp4',
    alt: 'Vela Bay Video Tour',
    span: 'single',
  },
  {
    type: 'image' as const,
    src: 'https://d3h1y9jvvpl9bt.cloudfront.net/assets/images/6de993b4-1765-45d3-a333-4d2d20f852f4/clean-shot-2026-01-23-at-14-36-07-2-x-1920w.webp',
    alt: 'Vela Bay Connectivity',
    span: 'single',
  },
];

type GalleryItem = typeof GALLERY_ITEMS[number];

interface LightboxProps {
  item: GalleryItem;
  onClose: () => void;
}

function Lightbox({ item, onClose }: LightboxProps) {
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 cursor-pointer text-white w-10 h-10 flex items-center justify-center hover:text-white/70 transition-colors"
        aria-label="Close"
      >
        <i className="ri-close-line text-3xl" />
      </button>
      <div
        className="max-w-5xl w-full max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === 'image' ? (
          <img
            src={item.src}
            alt={item.alt}
            className="max-w-full max-h-[80vh] object-contain rounded-xl"
          />
        ) : (
          <video controls autoPlay className="max-w-full max-h-[80vh] rounded-xl">
            <source src={item.src} type="video/mp4" />
            <track kind="captions" src="data:," srcLang="en" label="English" default />
          </video>
        )}
      </div>
    </div>
  );
}

export default function GallerySection() {
  const [lightbox, setLightbox] = useState<GalleryItem | null>(null);

  return (
    <section id="gallery" className="py-12 md:py-20 bg-[#F8F8F5]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <h4 id="gallery-title">
          <a href="#gallery" className="text-2xl md:text-3xl font-bold text-[#172C37] block mb-6 md:mb-8" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Gallery
          </a>
        </h4>

        {/* First: full-width */}
        <button
          onClick={() => setLightbox(GALLERY_ITEMS[0])}
          className="block w-full mb-4 overflow-hidden rounded-2xl cursor-pointer group"
        >
          <div className="w-full h-52 sm:h-72 md:h-[460px] relative overflow-hidden">
            <img
              src={GALLERY_ITEMS[0].src}
              alt={GALLERY_ITEMS[0].alt}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading={undefined}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-[#172C37] px-4 py-2 rounded-full text-sm font-semibold">
                View Full Size
              </span>
            </div>
          </div>
        </button>

        {/* Row: double + single */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Double span item */}
          <button
            onClick={() => setLightbox(GALLERY_ITEMS[1])}
            className="md:col-span-2 overflow-hidden rounded-2xl cursor-pointer group"
          >
            <div className="w-full h-52 md:h-72 relative overflow-hidden">
              <img
                src={GALLERY_ITEMS[1].src}
                alt={GALLERY_ITEMS[1].alt}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-[#172C37] px-4 py-2 rounded-full text-sm font-semibold">
                  View Full Size
                </span>
              </div>
            </div>
          </button>

          {/* Video */}
          <div className="md:col-span-1 overflow-hidden rounded-2xl">
            <div className="w-full h-52 md:h-72 relative">
              <video
                controls
                playsInline
                className="w-full h-full object-cover rounded-2xl"
                aria-label="Vela Bay video tour"
                poster={GALLERY_ITEMS[0].src}
              >
                <source src={GALLERY_ITEMS[2].src} type="video/mp4" />
                <track kind="captions" src="data:," srcLang="en" label="English" default />
              </video>
            </div>
          </div>
        </div>

        {/* Last: connectivity image */}
        <button
          onClick={() => setLightbox(GALLERY_ITEMS[3])}
          className="block w-full mt-4 overflow-hidden rounded-2xl cursor-pointer group"
        >
          <div className="w-full h-48 md:h-80 relative overflow-hidden">
            <img
              src={GALLERY_ITEMS[3].src}
              alt={GALLERY_ITEMS[3].alt}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-[#172C37] px-4 py-2 rounded-full text-sm font-semibold">
                View Full Size
              </span>
            </div>
          </div>
        </button>
      </div>

      {lightbox && <Lightbox item={lightbox} onClose={() => setLightbox(null)} />}
    </section>
  );
}
